import enMessages from '../../../../translations/en.json';
import thMessages from '../../../../translations/th.json';
import frMessages from '../../../../translations/fr.json';
import deMessages from '../../../../translations/de.json';
import esMessages from '../../../../translations/es.json';

const normalizeTitleCandidate = value => {
  return String(value || '')
    .trim()
    .replace(/[.!]+$/g, '')
    .toLowerCase();
};

const collectNormalizedTitles = values => {
  return new Set(values.filter(Boolean).map(normalizeTitleCandidate));
};

const FAILED_PAYMENT_TRANSITIONS = new Set([
  'transition/expire-payment',
  'transition/operator-expire-payment',
  'transition/payment-failed',
]);
const EXPIRED_REQUEST_TRANSITIONS = new Set([
  'transition/acceptance-window-passed',
  'transition/operator-acceptance-window-passed',
  'transition/pay-later-acceptance-window-passed',
  'transition/operator-pay-later-acceptance-window-passed',
  'transition/auto-decline-booking-time-starts',
  'transition/expire',
]);
const PAYMENT_WINDOW_PASSED_TRANSITIONS = new Set([
  'transition/payment-window-passed',
  'transition/operator-payment-window-passed',
]);
const PAYOUT_COMPLETE_TRANSITIONS = new Set([
  'transition/payout-complete',
  'transition/operator-payout-complete',
]);

const TRANSITION_DEDUPE_KEY_REGEX = /^tx:[^:]+:(transition\/[^:]+)(?::|$)/;
const LEGACY_PROVIDER_PENDING_PAYMENT_TITLES = collectNormalizedTitles(
  [
    enMessages['TransactionPage.default-booking.provider.pending-payment.title'],
    thMessages['TransactionPage.default-booking.provider.pending-payment.title'],
    frMessages['TransactionPage.default-booking.provider.pending-payment.title'],
    deMessages['TransactionPage.default-booking.provider.pending-payment.title'],
    esMessages['TransactionPage.default-booking.provider.pending-payment.title'],
    enMessages['TransactionPage.default-purchase.provider.pending-payment.title'],
    thMessages['TransactionPage.default-purchase.provider.pending-payment.title'],
    frMessages['TransactionPage.default-purchase.provider.pending-payment.title'],
    deMessages['TransactionPage.default-purchase.provider.pending-payment.title'],
    esMessages['TransactionPage.default-purchase.provider.pending-payment.title'],
  ]
);
const HIDDEN_PAYMENT_REMINDER_TITLES = collectNormalizedTitles(
  [
    enMessages['Notifications.paymentReminder.title'],
    thMessages['Notifications.paymentReminder.title'],
    frMessages['Notifications.paymentReminder.title'],
    deMessages['Notifications.paymentReminder.title'],
    esMessages['Notifications.paymentReminder.title'],
    enMessages['BookingComponent.notifications.paymentPending.title'],
    thMessages['BookingComponent.notifications.paymentPending.title'],
    'Complete payment to confirm your booking',
    'Complete your payment now to confirm your booking.',
    'ชำระเงินให้เสร็จสิ้นเพื่อยืนยันการจองของคุณ',
    'ชำระเงินตอนนี้เพื่อยืนยันการจองของคุณ',
  ]
);
const ACCEPTED_PENDING_PAYMENT_CUSTOMER_TITLE_MARKERS = Array.from(
  collectNormalizedTitles(
    [
      enMessages['Notifications.bookingAcceptedPendingPaymentCustomer.title'],
      thMessages['Notifications.bookingAcceptedPendingPaymentCustomer.title'],
      frMessages['Notifications.bookingAcceptedPendingPaymentCustomer.title'],
      deMessages['Notifications.bookingAcceptedPendingPaymentCustomer.title'],
      esMessages['Notifications.bookingAcceptedPendingPaymentCustomer.title'],
    ].map(value => {
      const marker = String(value || '').split('{providerName}').pop();
      return String(marker || value || '').replace(/^¡\s*/, '').trim();
    })
  )
);
const SELF_INQUIRY_TITLES = collectNormalizedTitles(
  [
    enMessages['Notifications.inquirySent.title'],
    thMessages['Notifications.inquirySent.title'],
    frMessages['Notifications.inquirySent.title'],
    deMessages['Notifications.inquirySent.title'],
    esMessages['Notifications.inquirySent.title'],
  ]
);

const parseMaybeJsonObject = value => {
  if (typeof value !== 'string') {
    return null;
  }

  const text = value.trim();
  if (!text.startsWith('{') || !text.endsWith('}')) {
    return null;
  }

  try {
    const parsed = JSON.parse(text);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const getNotificationTitleCandidates = notification => {
  const values = [notification?.headline, notification?.title_i18n, notification?.titleI18n];

  return values.reduce((collected, value) => {
    if (!value) {
      return collected;
    }

    if (typeof value === 'object' && !Array.isArray(value)) {
      return [...collected, ...Object.values(value)];
    }

    const parsed = parseMaybeJsonObject(value);
    if (parsed) {
      return [...collected, ...Object.values(parsed)];
    }

    return [...collected, value];
  }, []);
};

export const getNotificationTransitionName = notification => {
  const dedupeKey = String(notification?.dedupeKey || '').trim();
  const match = dedupeKey.match(TRANSITION_DEDUPE_KEY_REGEX);
  return match?.[1] || null;
};

export const isProviderNotificationTarget = notification => {
  const targetUrl = String(notification?.targetUrl || '').trim();
  return targetUrl.startsWith('/sale/');
};

export const isPaymentSuccessNotification = notification => {
  const transitionName = String(getNotificationTransitionName(notification) || '').toLowerCase();
  return transitionName.includes('confirm-payment');
};

export const isRequestPaymentNotification = notification => {
  const transitionName = String(getNotificationTransitionName(notification) || '').toLowerCase();
  return transitionName.includes('request-payment');
};

export const isInquiryNotification = notification => {
  return (
    notification?.type === 'booking.requested' &&
    String(getNotificationTransitionName(notification) || '').toLowerCase() === 'transition/inquire'
  );
};

export const isSelfInquiryNotification = notification => {
  if (!isInquiryNotification(notification)) {
    return false;
  }

  const hasInquirySentCopy = getNotificationTitleCandidates(notification).some(value =>
    SELF_INQUIRY_TITLES.has(normalizeTitleCandidate(value))
  );

  if (hasInquirySentCopy) {
    return true;
  }

  const targetUrl = String(notification?.targetUrl || '').trim();
  return !!targetUrl && !isProviderNotificationTarget(notification);
};

export const isPayoutCompleteNotification = notification => {
  if (notification?.type === 'booking.hostPayout') {
    return true;
  }

  const transitionName = getNotificationTransitionName(notification);
  return !!transitionName && PAYOUT_COMPLETE_TRANSITIONS.has(transitionName);
};

export const isLegacyHostPayoutNotification = notification => {
  return (
    notification?.type === 'booking.updated' &&
    isProviderNotificationTarget(notification) &&
    isPayoutCompleteNotification(notification)
  );
};

export const isLegacyCustomerPayoutNotification = notification => {
  return (
    notification?.type === 'booking.updated' &&
    !isProviderNotificationTarget(notification) &&
    isPayoutCompleteNotification(notification)
  );
};

export const isHostPayoutNotification = notification => {
  return notification?.type === 'booking.hostPayout' || isLegacyHostPayoutNotification(notification);
};

export const getNotificationNavigationTarget = notification => {
  if (isHostPayoutNotification(notification)) {
    return '/dashboard';
  }

  const targetUrl = String(notification?.targetUrl || '').trim();
  if (targetUrl) {
    return targetUrl;
  }

  return null;
};

export const isFailedPaymentNotification = notification => {
  if (notification?.type === 'booking.paymentNotConfirmed') {
    return true;
  }

  const transitionName = getNotificationTransitionName(notification);
  return !!transitionName && FAILED_PAYMENT_TRANSITIONS.has(transitionName);
};

export const isPaymentWindowPassedNotification = notification => {
  const transitionName = getNotificationTransitionName(notification);
  return !!transitionName && PAYMENT_WINDOW_PASSED_TRANSITIONS.has(transitionName);
};

export const isCreativeAcceptedPendingPaymentNotification = notification => {
  if (
    notification?.type !== 'booking.accepted' ||
    isProviderNotificationTarget(notification) ||
    !String(notification?.targetUrl || '').trim().startsWith('/order/')
  ) {
    return false;
  }

  return getNotificationTitleCandidates(notification).some(value =>
    ACCEPTED_PENDING_PAYMENT_CUSTOMER_TITLE_MARKERS.some(marker =>
      normalizeTitleCandidate(value).includes(marker)
    )
  );
};

export const isExpiredRequestNotification = notification => {
  if (notification?.type === 'booking.requestExpired') {
    return true;
  }

  const transitionName = getNotificationTransitionName(notification);
  return !!transitionName && EXPIRED_REQUEST_TRANSITIONS.has(transitionName);
};

export const isLegacyProviderPendingPaymentNotification = notification => {
  if (
    notification?.type !== 'booking.requested' ||
    !isProviderNotificationTarget(notification) ||
    !isRequestPaymentNotification(notification)
  ) {
    return false;
  }

  return getNotificationTitleCandidates(notification).some(value =>
    LEGACY_PROVIDER_PENDING_PAYMENT_TITLES.has(normalizeTitleCandidate(value))
  );
};

export const isHiddenPaymentReminderNotification = notification => {
  if (notification?.type === 'booking.paymentReminder') {
    return true;
  }

  if (
    notification?.type === 'booking.requested' &&
    !isProviderNotificationTarget(notification) &&
    isRequestPaymentNotification(notification)
  ) {
    return getNotificationTitleCandidates(notification).some(value =>
      HIDDEN_PAYMENT_REMINDER_TITLES.has(normalizeTitleCandidate(value))
    );
  }

  return false;
};
